var async = require("async");
const extend = require("extend");


/*
 * The same person has different roles in a event. The data about this is scattered throughout the database.
 * The goal of this module to to make it easy to deal with this scattered data.
 *
 * We do this by exposing the following concepts...
 *      Person
 *          A person object represents a "real person".
 *          It acts as a central point which combines related data for that person from different parts of the db.
 *          If you have an Person object, you can easily r/w on the person's attributes without worrying about
 *              where in the database the writes will need to go
 *
 *      People
 *          A people object represents a set of person objects.
 *          Its acts as a primary mechanism to deal with searching, sorting and filtering a list of people.
 *
 */

import PersonAlias from "./PersonAlias";
//var PersonAlias = require("./PersonAlias");
import * as person from "./person";
import _w from "./wrapper";
import FirebaseDataSource from "./firebaseDataSource";

import {promisify, promisifyInverse} from "../promisify/index";

/**
 * Constants
 */
const aliasTagKey = "aliasGroup";
const householdTagKey = "household";
const isHeadOfHouseholdTagKey = "isHeadOfHousehold";


/**
 * People - Represent a collection of Person objects
 *
 * @param listener An object which implements onChildAdded, onChildChanged, onChildRemoved, onErr as functions.
 * @param options An object which contains the writer sets: inviteeWriter and userProfileWriter
 * @constructor
 */
var People = function (listener, options) {
    var This = this;
    options = options || {};

    This._cache = {};
    This._people = {};

    // This will hold raw invitee objects + back pointer to the personId it belongs to.
    This._invitees = {};
    This._inviteeWriter = options.inviteeWriter || null;

    // This will hold raw userProfile objects + back pointer to the personId it belongs to.
    This._userProfiles = {};
    This._userProfileWriter = options.userProfileWriter || null;

    This._rsvpStrictNameMatch = options.rsvpStrictNameMatch || null;
    //This will hold raw global user profiles + back pointer to the personId it belongs to
    This._globalProfiles = {};

    // The listener for Person related events (onChild*)
    This._listener = listener;

};

(People as any).makePopulatedPeopleClass = function (fb) {

    const dataSource = new FirebaseDataSource(fb);

    const inviteeSet = dataSource.getInviteeSetSource();
    const userSetSource = dataSource.getUserSetSource();

    const people = new People({
        onChildAdded(){},
        onChildChanged(){}
    }, {
        inviteeWriter: inviteeSet,
        userProfileWriter: userSetSource
    });

    return Promise.all([
        inviteeSet.addOnceListener(people.getInviteeListener()),
        userSetSource.addOnceListener(people.getProfileListener())
    ])
        .then(() => people);

};


/**
 * Cache a value tied to the current state of the people class.
 * The cache is automatically cleared when the people class state changed
 * @param key
 * @param value
 */
People.prototype.setCachedValue = function (key, value) {
    var This = this;
    if (typeof key == "undefined") {
        return; //undefined
    }

    if (typeof value == "undefined") {
        delete This._cache[key];
        return;
    }

    //Note we use json so that we dont have to worry about mutation after storing in cache
    This._cache[key] = JSON.stringify(value);
};

/**
 * Get the cached value tied to the current state of the people class.
 * The cache is automatically cleared when the people class state changed
 * @param key
 * @returns {*}
 */
People.prototype.getCachedValue = function (key) {
    var This = this;
    if (typeof key == "undefined" || !(key in This._cache)) {
        return;
    }

    //Note we use json so that we dont have to worry about mutation after storing in cache
    return JSON.parse(This._cache[key]);
};

/**
 * Return the listeners the clients of this class can use to send in invitee profiles
 * @returns {Object} Live Set listener
 */
People.prototype.getInviteeListener = function () {
    var This = this;
    return {
        onChildAdded: This._inviteeAdded.bind(This),
        onChildChanged: This._inviteeChanged.bind(This),
        onChildRemoved: This._inviteeRemoved.bind(This),
        onErr: This._onErr.bind(This)
    };
};

/**
 * Return the listener the clients of this class can use to send in user profiles
 * @returns {Object} Live Set listener
 */
People.prototype.getProfileListener = function () {
    var This = this;
    return {
        onChildAdded: This._userProfileAdded.bind(This),
        onChildChanged: This._userProfileChanged.bind(This),
        onChildRemoved: This._userProfileRemoved.bind(This),
        onErr: This._onErr.bind(This)
    };
};

/**
 * Returns the listener which the clients of this class can use to send in global (non event specific) profile
 * information for users
 *
 * @returns {Object} Live Value Listener
 */
People.prototype.getGlobalProfilesListener = function () {
    var This = this;
    return {
        onValueChanged: This._globalProfilesChanged.bind(This),
        onErr: This._onErr.bind(This)
    };
};

People.prototype._inviteeAdded = function (source, inviteeKey, value) {
    var This = this;

    This._cache = {};

    //Create a invitee wrapper
    var inviteeWrapper = _w.create(value);

    //Find the person key we should use
    var personKey =
        _w.resolveValuePath(inviteeWrapper, _w.invitees.getTagPath(aliasTagKey)) ||
        "person-invitee" + inviteeKey;

    //Create or get the person object for this key.
    var personObj = This._people[personKey] || person.create();

    //Set the links
    person.linkInviteeKey(personObj, inviteeKey);
    person.clearCache(personObj);
    _w.linkPersonKey(inviteeWrapper, personKey);

    //Add to the sets and fire events. If the person already exists, we just fire the events
    //else we add it to the person set as well.
    This._invitees[inviteeKey] = inviteeWrapper;
    if (This._people[personKey]) {
        This._listener.onChildChanged(this, personKey, new PersonAlias(this, personKey));
        return;
    }
    This._people[personKey] = personObj;
    return This._listener.onChildAdded(this, personKey, new PersonAlias(this, personKey));
};

People.prototype._inviteeChanged = function (source, inviteeKey, value) {
    var This = this;

    This._cache = {};

    //Get the wrapper object
    var inviteeWrapper = This._invitees[inviteeKey];

    //Get the last personKey and see if it has changed.
    var oldPersonKey = _w.getPersonKey(inviteeWrapper);
    var newPersonKey =
        _w.resolveValuePath(_w.create(value), _w.invitees.getTagPath(aliasTagKey)) ||
        "person-invitee" + inviteeKey;

    //If the person key has changed...
    if (oldPersonKey.localeCompare(newPersonKey) !== 0) {
        //then remove the old invitee and add the new one
        This._inviteeRemoved(source, inviteeKey, _w.getValue(inviteeWrapper));
        This._inviteeAdded(source, inviteeKey, value);
        return;
    }
    //else just update the value and fire the listener
    _w.setValue(inviteeWrapper, value);
    person.clearCache(This._people[newPersonKey]);
    This._listener.onChildChanged(this, newPersonKey, new PersonAlias(this, newPersonKey));
};

People.prototype._inviteeRemoved = function (source, inviteeKey) {
    var This = this;

    This._cache = {};

    //Unlink this invitee from the person
    var inviteeObj = This._invitees[inviteeKey];
    var personKey = _w.getPersonKey(inviteeObj);
    var personObj = This._people[personKey];
    _w.unlinkPersonKey(inviteeObj);
    person.unlinkInviteeKey(personObj, inviteeKey);
    person.clearCache(personObj);
    delete This._invitees[inviteeKey];

    return This._postRemovedFireChangedOrRemoved(personKey);
};

People.prototype._userProfileAdded = function (source, profileKey, value) {
    var This = this;

    This._cache = {};

    //Create a user profile wrapper
    var userProfileWrapper = _w.create(value);

    //Find the person key we should use
    var personKey =
        _w.resolveValuePath(userProfileWrapper, _w.userProfiles.getTagPath(aliasTagKey)) ||
        "person-profile-" + profileKey;

    //Create a new person or get the already existing one
    var personObj = This._people[personKey] || person.create();

    //Set the links
    person.linkUserProfileKey(personObj, profileKey);
    person.clearCache(personObj);
    _w.linkPersonKey(userProfileWrapper, personKey);

    //Add to the relevant sets and fire events.
    //If we are adding to a preexisting person, then only fire the child changed
    This._userProfiles[profileKey] = userProfileWrapper;
    if (This._people[personKey]) {
        return This._listener.onChildChanged(this, personKey, new PersonAlias(this, personKey));
    }

    This._people[personKey] = personObj;
    return This._listener.onChildAdded(this, personKey, new PersonAlias(this, personKey));
};

People.prototype._userProfileChanged = function (source, profileKey, value) {
    var This = this;

    This._cache = {};

    //Get the wrapper object
    var profileWrapper = This._userProfiles[profileKey];

    //Get the last personKey and see if it has changed
    var oldPersonKey = _w.getPersonKey(profileWrapper);
    var newPersonKey =
        _w.resolveValuePath(_w.create(value), _w.userProfiles.getTagPath(aliasTagKey)) ||
        "person-profile-" + profileKey;

    //If the person key has changed...
    if (oldPersonKey.localeCompare(newPersonKey) !== 0) {
        //then fire removed for the old profile and add the new one
        This._userProfileRemoved(source, profileKey, _w.getValue(profileWrapper));
        This._userProfileAdded(source, profileKey, value);
        return;
    }
    //else just update the value and fire the listener
    _w.setValue(profileWrapper, value);
    person.clearCache(This._people[newPersonKey]);
    This._listener.onChildChanged(this, newPersonKey, new PersonAlias(this, newPersonKey));
};

People.prototype._userProfileRemoved = function (source, profileKey) {
    var This = this;

    This._cache = {};

    //Unlink this user profile from the person
    var profileObj = This._userProfiles[profileKey];
    var personKey = _w.getPersonKey(profileObj);
    var personObj = This._people[personKey];
    _w.unlinkPersonKey(profileObj);
    person.unlinkUserProfileKey(personObj, profileKey);
    person.clearCache(personObj);
    delete This._userProfiles[profileKey];

    return This._postRemovedFireChangedOrRemoved(personKey);

};

People.prototype._postRemovedFireChangedOrRemoved = function (personKey) {
    var This = this;
    //The innards of a person have been removed!
    //Such things either change the person if it does not
    var personObj = This._people[personKey];

    var willPersonSurvive = !person.isEmpty(personObj);

    if (willPersonSurvive) {
        This._listener.onChildChanged(this, personKey, new PersonAlias(this, personKey));
        return;
    }

    //Ok we need to remove this person
    delete This._people[personKey];
    This._listener.onChildRemoved(this, personKey);
};


People.prototype._globalProfileAdded = function (source, key, value) {
    var This = this;
    This._cache = {};
    This._globalProfiles[key] = _w.create(value);

    //Check if there is already a user profile for this key
    //if not there are no events to send
    if (!(key in This._userProfiles)) {
        return;
    }


    var wUser = This._userProfiles[key];
    var personKey = _w.getPersonKey(wUser);
    person.clearCache(This._people[personKey]);
    This._listener.onChildChanged(this, personKey, new PersonAlias(this, personKey));
};

People.prototype._globalProfileChanged = function (source, key, value) {
    var This = this;
    This._cache = {};
    This._globalProfiles[key] = _w.create(value);

    //Check if there is already a user profile for this key
    //if not there are no events to send
    if (!(key in This._userProfiles)) {
        return;
    }


    var wUser = This._userProfiles[key];
    var personKey = _w.getPersonKey(wUser);
    person.clearCache(This._people[personKey]);
    This._listener.onChildChanged(this, personKey, new PersonAlias(this, personKey));
};

People.prototype._globalProfileRemoved = function (source, key) {
    var This = this;
    This._cache = {};
    delete This._globalProfiles[key];

    //Check if there is already a user profile for this key
    //if not there are no events to send
    if (!(key in This._userProfiles)) {
        return;
    }

    var wUser = This._userProfiles[key];
    var personKey = _w.getPersonKey(wUser);
    person.clearCache(This._people[personKey]);
    This._listener.onChildChanged(this, personKey, new PersonAlias(this, personKey));
};

People.prototype._globalProfilesChanged = function (source, newValues) {
    var This = this;
    var newKeys = Object.keys(newValues);
    var oldKeys = Object.keys(This._globalProfiles);

    var _changed = This._globalProfileChanged.bind(This, This);
    var _added = This._globalProfileAdded.bind(This, This);
    var _removed = This._globalProfileRemoved.bind(This, This);

    //Look for added and changed values
    newKeys.forEach(function (newKey) {
        var f = (newKey in This._globalProfiles) ? _changed : _added;

        var newValue = newValues[newKey];
        f(newKey, newValue);
    });

    //Look for removed keys
    oldKeys.forEach(function (oldKey) {
        if (oldKey in newValues) {
            return;
        }

        var value = This._globalProfiles[oldKey];
        _removed(oldKey, value);

    });

};

People.prototype._onErr = function (err) {
    console.error(err);
};


/**
 * Maps through all alias using alias type specific map functions and then reduces all results with one function
 * @param personKey
 *
 * @param {Object} mapFuncs - Set of find/filtering one for more functions which take a key and return a boolean
 * @param {inviteeMapFunc} mapFuncs.inviteeFunc - this will be called for all invitees
 * @param {userProfileMapFunc} mapFuncs.userProfileFunc - this will be called for all userProfiles
 *
 * @param {Function} reduceFunc - A function to reduce the return values from the map functions
 * @private
 *
 */
People.prototype._mapReduceSync = function (personKey, mapFuncs, reduceFunc, initialValue) {
    var This = this;
    var personObj = This._people[personKey];
    var ret = initialValue;
    if (!personObj) {
        return ret;
    }

    var inviteeKeys = person.getInviteeKeys(personObj);
    var inviteeMapFunc = mapFuncs.inviteeFunc;
    if (inviteeKeys && inviteeMapFunc) {
        ret = inviteeKeys.map(inviteeMapFunc).reduce(reduceFunc, ret);
    }

    var userProfileKeys = person.getUserProfileKeys(personObj);
    var userProfileFunc = mapFuncs.userProfileFunc;
    if (userProfileKeys && userProfileFunc) {
        ret = userProfileKeys.map(userProfileFunc).reduce(reduceFunc, ret);
    }

    return ret;

};


People.prototype._applyToPersonAsync = function (personKey, funcs, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var jobs = [];

    var inviteeKeys = person.getInviteeKeys(personObj);
    var inviteeFunc = funcs.inviteeFunc;
    if (inviteeKeys && inviteeFunc) {
        jobs.push(async.each.bind(async, inviteeKeys, inviteeFunc));
    }

    var userProfileKeys = person.getUserProfileKeys(personObj);
    var userProfileFunc = funcs.userProfileFunc;
    if (userProfileKeys && userProfileFunc) {
        jobs.push(async.each.bind(async, userProfileKeys, userProfileFunc));
    }

    return async.parallel(jobs, callback);
};

// --Any returns the callback with an error only if ALL apply calls fail
People.prototype._applyToPersonAsyncAny = function (personKey, funcs, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var jobs = [];

    var inviteeKeys = person.getInviteeKeys(personObj);
    var inviteeFunc = funcs.inviteeFunc;
    if (inviteeKeys && inviteeFunc) {
        let reflectedInviteeFunc = async.reflect(inviteeFunc);
        jobs = jobs.concat(inviteeKeys.map(function (inviteeKey) {
            return function (callback) {
                return reflectedInviteeFunc(inviteeKey, callback);
            };
        }));
    }

    var userProfileKeys = person.getUserProfileKeys(personObj);
    var userProfileFunc = funcs.userProfileFunc;
    if (userProfileKeys && userProfileFunc) {
        let reflectedUserProfileFunc = async.reflect(userProfileFunc);
        jobs = jobs.concat(userProfileKeys.map(function (userProfileKey) {
            return function (callback) {
                return reflectedUserProfileFunc(userProfileKey, callback);
            };
        }));
    }

    return async.parallel(jobs, (err, results) => {
        // here we handle errors and return one if the number of errors is equal to that of the apply calls made
        let errors = results.filter(result => !!result.error);

        if (errors.length === results.length) {
            let compoundError = new Error(errors.reduce((errorStrings, error) => {
                return errorStrings + ", " + error.message;
            }, ""));

            return callback(compoundError);
        }

        return callback(null);
    });
};


/***
 * This function looks at all aliases for a person and picks the "right" alias's property.
 * @param personKey
 * @param paths Should contain inviteePath and/or userProfilePath for the propoerty you are interested in
 * @returns {*} Winning value for the property.
 * @private
 */
People.prototype._resolveLatestValuePath = function (personKey, paths) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return null;
    }


    //List of places we want to search - in decreasing order or priority.
    var searches = [{
        keys: person.getUserProfileKeys(personObj),
        setObj: This._userProfiles,
        path: paths.userProfilePath,
        timestampPath: paths.userProfilePath && _w.userProfiles.getTimestampPath(paths.userProfilePath)
    }, {
        keys: person.getInviteeKeys(personObj),
        setObj: This._invitees,
        path: paths.inviteePath,
        timestampPath: paths.inviteePath && _w.invitees.getTimestampPath(paths.inviteePath)
    }, {
        keys: person.getUserProfileKeys(personObj),
        setObj: This._globalProfiles,
        path: paths.globalProfilePath,
        timestampPath: null
    }];

    /*
     For each backing alias we read the property and convert it to the following format
     {priority, value, timestamp}
         value is the value of the property
         priority is the priority used for finding the winner if timestamps match
             higher is preferred
     */
    var mappedObjs = [];
    searches.forEach(function (searchInfo) {

        if (!searchInfo.keys || !searchInfo.path) {
            return;
        }

        searchInfo.keys.forEach(function (k) {
            if (!(k in searchInfo.setObj)) {
                return;
            }

            var w = searchInfo.setObj[k];

            //check if the question was not answered
            if (!_w.hasValuePath(w, searchInfo.path)) {
                //if timestamp is not set, then the question was not answered.
                if (!searchInfo.timestampPath || !_w.hasValuePath(w, searchInfo.timestampPath)) {
                    return;
                }
            }

            /**
             * timestamp is preferred over the priority of the key
             * for both priority and timestamp, a higher value is preferred
             * more over timestamp will most sure be higher than priority
             *
             */
            var priority;

            if (searchInfo.timestampPath && _w.hasValuePath(w, searchInfo.timestampPath)) {
                priority = _w.resolveValuePath(w, searchInfo.timestampPath);
            }
            else if (k.indexOf("facebook") === 0) {
                priority = 1000;
            }
            else if (k.indexOf("auth0") === 0) {
                priority = 800;
            }
            else if (k.indexOf("google") === 0) {
                priority = 600;
            }
            else {
                priority = 400;
            }

            var ret = {
                priority: priority,
                value: _w.resolveValuePath(w, searchInfo.path),
            };

            mappedObjs.push(ret);
        });
    });

    var retMappedObj = mappedObjs.reduce(function (acc, curr) {
        if (!acc) {
            return curr;
        }

        if (!curr) {
            return acc;
        }

        return ((curr.priority > acc.priority) ? curr : acc);
    }, null);

    if (!retMappedObj) {
        return null;
    }

    return retMappedObj.value;
};


/**
 * Goes through all aliases and finds the first one which matches and map that using the provided function
 * Currently we start with the user profiles and then fall back to invitees
 * @param personKey
 *
 * @param {Object} filterFuncs - Set of find/filtering one for more functions which take a key and return a boolean
 * @param {inviteeFilterFunc} filterFuncs.inviteeFunc - function to find the invitee key
 * @param {userProfileFilterFunc} filterFuncs.userProfileFunc - function to find user profile key
 *
 * @param {Object} mapFuncs - Set of find/filtering one for more functions which take a key and return a boolean
 * @param {inviteeMapFunc} mapFuncs.inviteeFunc - this will be called if a invitee key is selected
 * @param {userProfileMapFunc} mapFuncs.userProfileFunc - this will be called if a userProfile key is selected
 *
 * @private
 *
 */
People.prototype._findAliasAndApplySync = function (personKey, filterFuncs, mapFuncs) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return null;
    }
    var userProfileKeys = person.getUserProfileKeys(personObj);
    var userProfileKey = userProfileKeys.find(filterFuncs.userProfileFunc);

    if (userProfileKey) {
        return mapFuncs.userProfileFunc(userProfileKey);
    }

    var inviteeKeys = person.getInviteeKeys(personObj);
    var inviteeKey = inviteeKeys.find(filterFuncs.inviteeFunc);

    if (inviteeKey) {
        return mapFuncs.inviteeFunc(inviteeKey);
    }

    return null;
};

People.prototype._findAliasAndApplySyncCached = function (personKey, cacheKey, filterFuncs, mapFuncs) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return null;
    }

    const cachedValue = person.getCachedValue(personObj, cacheKey);
    if (typeof cachedValue != "undefined") {
        return cachedValue;
    }

    const newCachedValue = This._findAliasAndApplySync(personKey, filterFuncs, mapFuncs);
    person.setCachedValue(personObj, cacheKey, newCachedValue);
    return newCachedValue;
};


/**
 * Basic Set Apis
 */
People.prototype.getPersonAtKey = function (personKey) {
    var This = this;
    var peopleObj = This._people[personKey];
    if (!peopleObj) {
        return null;
    }

    return new PersonAlias(This, personKey);
};

People.prototype._getInviteeKeys = function (personKey) {
    var This = this;
    if (!(personKey in This._people)) {
        return null;
    }
    var personObj = This._people[personKey];
    return person.getInviteeKeys(personObj);
};

People.prototype._getUserProfileKeys = function (personKey) {
    var This = this;
    if (!(personKey in This._people)) {
        return null;
    }
    var personObj = This._people[personKey];
    return person.getUserProfileKeys(personObj);
};

People.prototype._getGlobalProfileKeys = function (personKey) {
    var This = this;

    var userProfileKeys = This._getUserProfileKeys(personKey);
    if (!userProfileKeys) {
        return userProfileKeys;
    }

    //user profile keys which also have a global profile
    return userProfileKeys
        .filter(function (k) { return (k in This._globalProfiles);});
};

People.prototype.getKeys = function () {
    var This = this;
    return Object.keys(This._people);
};

People.prototype._getFirstName = function (personKey) {
    var This = this;
    var name = This._getName(personKey);
    if (!name) {
        return name;
    }

    var nameComponents = name.split(" ");
    if (nameComponents.length <= 0) {
        return null;
    }

    return nameComponents[0].split("\xa0").join(" ");
};

People.prototype._getLastTimestamp = function (personKey) {
    var This = this;

    var latestReducer = function (acc, curr) {
        if (!acc && !curr) {
            return null;
        }

        if (acc && !curr) {
            return acc;
        }

        if (!acc && curr) {
            return curr;
        }

        return ((acc > curr) ? acc : curr);

    };

    var findLatestTimestamp = function (value) {
        return Object.keys(value)
            .filter(function (propName) {
                return propName.endsWith("-timestamp");
            })
            .map(function (propName) {
                var date = value[propName];
                if (!date || !(date instanceof Number || typeof date === "number")) {
                    return null;
                }

                return new Date(date as number);
            })
            .reduce(latestReducer, null);
    };

    var latestTimestamp = This._mapReduceSync(personKey, {
        inviteeFunc: function (k) {
            var wrapper = This._invitees[k];
            var value = _w.getValue(wrapper) || {};
            return findLatestTimestamp(value);

        },
        userProfileFunc: function (k) {
            var wrapper = This._userProfiles[k];
            var value = ((_w.getValue(wrapper) || {}).profile) || {};
            return findLatestTimestamp(value);
        }
    }, latestReducer, null);

    if (!latestTimestamp) {
        return null;
    }

    return latestTimestamp;
};

People.prototype._getLastName = function (personKey) {
    var This = this;
    var name = This._getName(personKey);
    if (!name) {
        return name;
    }

    var nameComponents = name.split(" ");
    if (nameComponents.length <= 1) {
        return null;
    }

    nameComponents.splice(0, 1);
    return nameComponents.join(" ").split("\xa0").join(" ");
};

People.prototype._getName = function (personKey) {
    var This = this;
    return This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getNamePath(),
        userProfilePath: _w.userProfiles.getNamePath()
    });
};

People.prototype._getEmail = function (personKey) {
    var This = this;
    var email = This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getEmailPath(),
        userProfilePath: _w.userProfiles.getEmailPath(),
        globalProfilePath: _w.globalProfiles.getEmailPath()
    });

    if (email) {
        return email;
    }
};

People.prototype._getEmailVerified = function (personKey) {
    var This = this;
    var emailVerified = This._resolveLatestValuePath(personKey, {
        inviteePath: null,
        userProfilePath: null,
        globalProfilePath: _w.globalProfiles.getEmailVerifiedPath()
    });

    if (emailVerified) {
        return emailVerified;
    }
};

People.prototype._getBadEmail = function (personKey) {
    var This = this;
    var badEmail = This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getBadEmailPath(),
        userProfilePath: _w.userProfiles.getBadEmailPath(),
    });

    if (badEmail) {
        return badEmail;
    }
};

People.prototype._getStatus = function (personKey) {
    var This = this;
    var status = This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getStatusPath(),
        userProfilePath: _w.userProfiles.getStatusPath()
    });

    if (status) {
        return status;
    }
};

People.prototype._getCreatedAt = function (personKey) {
    var This = this;
    var createdAt = This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getCreatedAtPath(),
        userProfilePath: _w.userProfiles.getCreatedAtPath()
    });
    if (createdAt) {
        return createdAt;
    }
};

People.prototype._getCreatedBy = function (personKey) {
    var This = this;
    var createdBy = This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getCreatedByPath(),
        userProfilePath: _w.userProfiles.getCreatedByPath()
    });
    if (createdBy) {
        return createdBy;
    }
};

People.prototype._getNameTimestamp = function (personKey) {
    var This = this;
    var nameTimestamp = This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getNameTimestampPath(),
        userProfilePath: _w.userProfiles.getNameTimestampPath()
    });
    if (nameTimestamp) {
        return nameTimestamp;
    }
};

People.prototype._getAvatar = function (personKey) {
    var This = this;
    return This._findAliasAndApplySyncCached(personKey, "avatar", {
        //find functions
        inviteeFunc: function (k) {
            return _w.hasValuePath(This._invitees[k], _w.invitees.getAvatarPath());
        },
        userProfileFunc: function (k) {
            return _w.hasValuePath(This._userProfiles[k], _w.userProfiles.getAvatarPath());
        }
    }, {
        //map functions
        inviteeFunc: function (k) {
            return _w.resolveValuePath(This._invitees[k], _w.invitees.getAvatarPath());
        },

        userProfileFunc: function (k) {
            return _w.resolveValuePath(This._userProfiles[k], _w.userProfiles.getAvatarPath());
        }
    });

};

People.prototype._setAvatar = function (personKey, avatar, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var setter = function (key, callback) {
        if (key in This._invitees) {
            This._inviteeWriter.changeChild(key, _w.invitees.getAvatarPath(), avatar, callback);
        }

        if (key in This._userProfiles) {
            This._userProfileWriter.changeChild(key, _w.userProfiles.getAvatarPath(), avatar, callback);
        }
    };

    This._applyToPersonAsyncAny(personKey, {
        inviteeFunc: setter,
        userProfileFunc: setter
    }, callback);
};

People.prototype._setName = function (personKey, firstName, lastName, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var nameComponents = [];
    if (firstName) {
        firstName = firstName.split(" ").join("\xa0");
        nameComponents.push(firstName);
    } else {
        firstName = This._getFirstName(personKey);
        firstName = firstName ? firstName.split(" ").join("\xa0") : firstName;
        nameComponents.push(firstName);
    }

    if (lastName) {
        lastName = lastName.split(" ").join("\xa0");
        nameComponents.push(lastName);
    } else {
        lastName = This._getLastName(personKey);
        lastName = lastName ? lastName.split(" ").join("\xa0") : lastName;
        if (lastName) {
            nameComponents.push(lastName);
        }
    }

    var name = nameComponents.join(" ");

    return This._applyToPersonAsyncAny(personKey, {
        inviteeFunc: function (inviteeKey, callback) {
            return This._inviteeWriter.changeChild(
                inviteeKey,
                _w.invitees.getNamePath(),
                name,
                callback
            );
        },

        userProfileFunc: function (profileKey, callback) {
            return async.parallel([

                This._userProfileWriter.changeChild.bind(
                    This._userProfileWriter,
                    profileKey,
                    _w.userProfiles.getNamePath(),
                    name
                ),

                This._userProfileWriter.changeChild.bind(
                    This._userProfileWriter,
                    profileKey,
                    _w.userProfiles.getDisplayNamePath(),
                    firstName || lastName || null
                )
            ], callback);
        }
    }, callback);
};

People.prototype._setFirstName = function (personKey, firstName, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }


    var nameComponents = [];
    if (firstName) {
        firstName = firstName.split(" ").join("\xa0");
        nameComponents.push(firstName);
    }

    // getLastName returns string without non-breaking space
    var lastName = This._getLastName(personKey);
    if (lastName) {
        lastName = lastName.split(" ").join("\xa0");
        nameComponents.push(lastName);
    }

    var name = nameComponents.join(" ");

    return This._applyToPersonAsyncAny(personKey, {
        inviteeFunc: function (inviteeKey, callback) {
            return This._inviteeWriter.changeChild(
                inviteeKey,
                _w.invitees.getNamePath(),
                name,
                callback
            );
        },

        userProfileFunc: function (profileKey, callback) {
            return async.parallel([

                This._userProfileWriter.changeChild.bind(
                    This._userProfileWriter,
                    profileKey,
                    _w.userProfiles.getNamePath(),
                    name
                ),

                This._userProfileWriter.changeChild.bind(
                    This._userProfileWriter,
                    profileKey,
                    _w.userProfiles.getDisplayNamePath(),
                    firstName || lastName || null
                )
            ], callback);
        }
    }, callback);
};

People.prototype._setLastName = function (personKey, lastName, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }


    var nameComponents = [];

    // getFirstName returns string without non-breaking space
    var firstName = This._getFirstName(personKey);
    if (firstName) {
        firstName = firstName.split(" ").join("\xa0");
        nameComponents.push(firstName);
    }

    if (lastName) {
        lastName = lastName.split(" ").join("\xa0");
        nameComponents.push(lastName);
    }

    var name = nameComponents.join(" ");

    return This._applyToPersonAsyncAny(personKey, {
        inviteeFunc: function (inviteeKey, callback) {
            return This._inviteeWriter.changeChild(
                inviteeKey,
                _w.invitees.getNamePath(),
                name,
                callback
            );
        },

        userProfileFunc: function (profileKey, callback) {
            return async.parallel([

                This._userProfileWriter.changeChild.bind(
                    This._userProfileWriter,
                    profileKey,
                    _w.userProfiles.getNamePath(),
                    name
                ),

                This._userProfileWriter.changeChild.bind(
                    This._userProfileWriter,
                    profileKey,
                    _w.userProfiles.getDisplayNamePath(),
                    firstName || lastName || null
                )
            ], callback);
        }
    }, callback);
};

People.prototype._setEmail = function (personKey, value, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var setter = function (key, callback) {
        if (key in This._invitees) {
            This._inviteeWriter.changeChild(key, _w.invitees.getEmailPath(), value, callback);
        }

        if (key in This._userProfiles) {
            This._userProfileWriter.changeChild(key, _w.userProfiles.getEmailPath(), value, callback);
        }
    };

    This._applyToPersonAsyncAny(personKey, {
        inviteeFunc: setter,
        userProfileFunc: setter
    }, callback);
};

People.prototype._setBadEmail = function (personKey, value, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var setter = function (key, callback) {
        if (key in This._invitees) {
            This._inviteeWriter.changeChild(key, _w.invitees.getBadEmailPath(), value, callback);
        }

        if (key in This._userProfiles) {
            This._userProfileWriter.changeChild(key, _w.userProfiles.getBadEmailPath(), value, callback);
        }
    };

    This._applyToPersonAsync(personKey, {
        inviteeFunc: setter,
        userProfileFunc: setter
    }, callback);
};

People.prototype._setStatus = function (personKey, value, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var setter = function (key, callback) {
        if (key in This._invitees) {
            This._inviteeWriter.changeChild(key, _w.invitees.getStatusPath(), value, callback);
        }

        if (key in This._userProfiles) {
            This._userProfileWriter.changeChild(key, _w.userProfiles.getStatusPath(), value, callback);
        }
    };

    This._applyToPersonAsync(personKey, {
        inviteeFunc: setter,
        userProfileFunc: setter
    }, callback);
};

People.prototype._setTag = function (personKey, tag, value, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var setter = function (key, callback) {
        if (key in This._invitees) {
            This._inviteeWriter.changeChild(key, _w.invitees.getTagPath(tag), value, callback);
        }

        if (key in This._userProfiles) {
            This._userProfileWriter.changeChild(key, _w.userProfiles.getTagPath(tag), value, callback);
        }
    };

    This._applyToPersonAsync(personKey, {
        inviteeFunc: setter,
        userProfileFunc: setter
    }, callback);
};

People.prototype._setHouseholdId = promisify(function (personKey, householdId, callback) {
    var This = this;
    return This._setTag(personKey, householdTagKey, householdId, callback);
});

People.prototype._setHousehold = function (personKey, household, callback) {
    var This = this;
    return This._setHouseholdId(personKey, household.id, callback);
};

People.prototype._setIsHeadOfHousehold = promisify(function (personKey, value, callback) {
    if(value === undefined) value = true;
    return this._setTag(personKey, isHeadOfHouseholdTagKey, value, callback);
});

People.prototype._setDetail = function (personKey, detail, value, callback) {
    var This = this;
    var personObj = This._people[personKey];
    if (!personObj) {
        return callback(new Error("Person not found!"));
    }

    var setter = function (key, callback) {
        if (key in This._userProfiles) {
            This._userProfileWriter.changeChild(key, _w.userProfiles.getDetailPath(detail), value, callback);
        }
        else if (key in This._invitees) {
            This._inviteeWriter.changeChild(key, _w.invitees.getDetailPath(detail), value, callback);
        }
    };

    return This._applyToPersonAsyncAny(personKey, {
        inviteeFunc: setter,
        userProfileFunc: setter
    }, callback);
};

People.prototype._setAnswer = function (personKey, question, answer, callback) {
    const This = this;
    const answerKey = question.answerKey;
    const storeValue = question.answerObjToJson(answer);

    return This._setDetail(personKey, answerKey, storeValue, callback);
};

People.prototype._assimilate = promisifyInverse(function (personKey, personOrPersonKey) {
    return Promise.resolve()
        .then(() => {
            let keyToAssimilate;
            if (personOrPersonKey instanceof PersonAlias) {
                keyToAssimilate = personOrPersonKey.getPersonKey();
            }
            else {
                keyToAssimilate = personOrPersonKey;
            }

            if (keyToAssimilate.localeCompare(personKey) === 0) {
                return;
            }

            //Keys of all people whome we should tag as duplicates
            var personKeysToModify = [];

            //Check if person already has a alias
            //if not create a new alias key and assign it to both people
            var aliasTagValue = this._getTag(personKey, aliasTagKey);
            if (!aliasTagValue) {
                personKeysToModify.push(personKey);
                aliasTagValue = "aliasGroup-" + personKey;
            }

            personKeysToModify.push(keyToAssimilate);

            const assimilate = promisify(callback => async.each(
                [personKey, keyToAssimilate],
                (pKey, callback) => this._setTag(pKey, aliasTagKey, aliasTagValue, callback),
                callback
            ));

            // For these props the main person should always keep the value
            // Here we are updating the timestamps of these props
            // (TODO find a better way to classify and recognize these props)
            const makeRecent = () => Promise.all([
                this._setIsHeadOfHousehold(personKey, this._getIsHeadOfHousehold(personKey)),
                this._setHouseholdId(personKey, this._getHouseholdId(personKey))
            ]);

            return makeRecent()
                .then(() => assimilate());
        });
});

People.prototype.findPersonWithUserId = function (userId) {
    if (!userId) {
        return null;
    }

    const personKeys = this.getKeys().filter(personKey => {
        const person = this.getPersonAtKey(personKey);
        const userProfileKeys = person.userProfileKeys || [];
        return userProfileKeys.includes(userId);
    });

    if (!personKeys.length) {
        return null;
    }

    return this.getPersonAtKey(personKeys[0]);
};

People.prototype._getTag = function (personKey, tagKey) {
    var This = this;
    return This._findAliasAndApplySyncCached(personKey, "tags/" + tagKey, {
        inviteeFunc: function (k) {
            var inviteeWrapper = This._invitees[k];
            return _w.hasValuePath(inviteeWrapper, _w.invitees.getTagPath(tagKey));
        },

        userProfileFunc: function (k) {
            var userProfileWrapper = This._userProfiles[k];
            return _w.hasValuePath(userProfileWrapper, _w.userProfiles.getTagPath(tagKey));
        }
    }, {
        inviteeFunc: function (k) {
            return _w.resolveValuePath(This._invitees[k], _w.invitees.getTagPath(tagKey));
        },
        userProfileFunc: function (k) {
            return _w.resolveValuePath(This._userProfiles[k], _w.userProfiles.getTagPath(tagKey));
        }
    });
};

People.prototype._getHouseholdId = function (personKey) {
    var This = this;
    return This._getTag(personKey, householdTagKey);
};

People.prototype._getHousehold = function (personKey, households) {
    return households.getHouseholdForPerson(this.getPersonAtKey(personKey));
};

People.prototype._getIsHeadOfHousehold = function (personKey) {
    return this._getTag(personKey, isHeadOfHouseholdTagKey) !== false;
};

People.prototype._getDetail = function (personKey, detailKey) {
    var This = this;
    return This._resolveLatestValuePath(personKey, {
        inviteePath: _w.invitees.getDetailPath(detailKey),
        userProfilePath: _w.userProfiles.getDetailPath(detailKey)
    });
};

People.prototype._getDetailTimestamp = function (personKey, detailKey) {

    const inviteePath = _w.invitees.getTimestampPath(_w.invitees.getDetailPath(detailKey));
    const userProfilePath = _w.userProfiles.getTimestampPath(_w.userProfiles.getDetailPath(detailKey));

    const inviteeFunc = (key) => _w.resolveValuePath(this._invitees[key], inviteePath);

    const userProfileFunc = (key) => _w.resolveValuePath(this._userProfiles[key], userProfilePath);

    const latestReducer = (acc, curr) => {
        if (!acc && !curr) {
            return null;
        }

        if (acc && !curr) {
            return acc;
        }

        if (!acc && curr) {
            return curr;
        }

        return ((acc > curr) ? acc : curr);

    };

    return this._mapReduceSync(personKey, {
        inviteeFunc: inviteeFunc,
        userProfileFunc: userProfileFunc
    }, latestReducer, null);
};

// Deprecated - use Answer object instead
People.prototype._getAnswer = function (personKey, question) {
    const This = this;
    const answerKey = question.answerKey;
    const rawAnswer = This._getDetail(personKey, answerKey);
    return question.answerJsonToObj(rawAnswer);
};

People.prototype._getAnswerJSON = function (personKey, question) {
    const answerKey = question.answerKey;
    return this._getDetail(personKey, answerKey);
};

People.prototype._getAnswerTimestamp = function (personKey, question) {
    const This = this;
    const answerKey = question.answerKey;
    return This._getDetailTimestamp(personKey, answerKey);
};

People.prototype._getWasEvicted = function (personKey) {
    var This = this;

    return This._mapReduceSync(personKey, {
        inviteeFunc: function (k) {
            var inviteeWrapper = This._invitees[k];
            if (!_w.hasValuePath(inviteeWrapper, _w.invitees.getWasEvictedPath())) {
                return false;
            }
            return _w.resolveValuePath(inviteeWrapper, _w.invitees.getWasEvictedPath());
        },

        userProfileFunc: function (k) {
            var userProfileWrapper = This._userProfiles[k];
            if (!_w.hasValuePath(userProfileWrapper, _w.userProfiles.getWasEvictedPath())) {
                return false;
            }
            return _w.resolveValuePath(userProfileWrapper, _w.userProfiles.getWasEvictedPath());
        }
    }, function (acc, curr) {
        return acc && curr;
    }, true);
};

People.prototype._getHasLeft = function (personKey) {
    var This = this;

    return This._mapReduceSync(personKey, {
        inviteeFunc: function (k) {
            var inviteeWrapper = This._invitees[k];
            if (!_w.hasValuePath(inviteeWrapper, _w.invitees.getHasLeftPath())) {
                return false;
            }
            return _w.resolveValuePath(inviteeWrapper, _w.invitees.getHasLeftPath());
        },

        userProfileFunc: function (k) {
            var userProfileWrapper = This._userProfiles[k];
            if (!_w.hasValuePath(userProfileWrapper, _w.userProfiles.getHasLeftPath())) {
                return false;
            }
            return _w.resolveValuePath(userProfileWrapper, _w.userProfiles.getHasLeftPath());
        }
    }, function (acc, curr) {
        return acc && curr;
    }, true);
};

People.prototype._ensureInviteeOrWritable = promisify(function (personKey, userId, callback) {

    //If already backed by an invitee, nothing to do!
    const inviteeKeys = this._getInviteeKeys(personKey);
    if (inviteeKeys && inviteeKeys.length > 0) {
        return callback(null);
    }

    //If userId is a match, nothing to do
    const userProfileKeys = this._getUserProfileKeys(personKey);
    if (userId && userProfileKeys.indexOf(userId) >= 0) {
        return callback(null);
    }

    //Else we create a new invitee
    let newTagForInvitee = this._getTag(personKey, aliasTagKey);
    if (!newTagForInvitee) {
        newTagForInvitee = personKey;
    }

    const tagsPath = _w.invitees.getTagPath(aliasTagKey);
    const inviteeObj = tagsPath.reduceRight((inviteeObj, pathComponent) => {
        const ret = {};
        ret[pathComponent] = inviteeObj;
        return ret;
    }, newTagForInvitee);

    this._inviteeWriter.addChild(inviteeObj, callback);
});

(People as any).evictedFilter = p => !(p.getWasEvicted() || p.getHasLeft());

People.prototype.allNonEvictedPeople = function () {
    return this.getKeys()
        .map(personKey => new PersonAlias(this, personKey))
        .filter(p => (People as any).evictedFilter(p));
};

People.prototype.allHouseholds = function () {
    const noHouse = [];
    const households = this.allNonEvictedPeople()
        .map(personAlias => ({ person: personAlias, household : this._getHouseholdId(personAlias.getPersonKey()) }) )
        .reduce((accumulator, obj) => {
            if(obj.household){
                var hh = accumulator[obj.household];
                hh ? hh.push(obj.person) : accumulator[obj.household] = [obj.person];
            }else{
                noHouse.push(obj.person);
            }
            return accumulator;
        }, {})
    households.total_households = Object.keys(households).length + noHouse.length;
    households.no_house = noHouse;
    return households;
}

People.prototype.allHeadsOfHousehold = function(){
    return this.allNonEvictedPeople()
        .filter( personAlias => personAlias.isHeadOfHousehold );
}

People.prototype._createPlusOne = promisify(function (householdId, options, callback) {
    const hhTagsPath = _w.invitees.getTagPath(householdTagKey);
    hhTagsPath.push(householdId);
    const iWithHHTag = hhTagsPath.reduceRight((iWithHHTag, pathComponent) => ({ [pathComponent] : iWithHHTag }));

    const hohTagsPath = _w.invitees.getTagPath(isHeadOfHouseholdTagKey);
    hohTagsPath.push(false);
    const iNotHoH = hohTagsPath.reduceRight((iNotHoH, pathComponent) => ({ [pathComponent] : iNotHoH }));

    const properties = ((options || {}).properties) || {};
    const invitee = extend(true, {}, iWithHHTag, iNotHoH, properties);
    this._inviteeWriter.addChild(invitee, callback);
});

People.prototype._getIsVisibleToGuestInGuestList = function (personKey) {
    let This = this;
    return (This._getUserProfileKeys(personKey).length > 0);
};

/**
 *
 * @callback inviteeFilterFunc
 * @param {string} key - Invitee key
 * @returns {bool} Result of the find operation on the key
 *
 * @callback userProfileFilterFunc
 * @param {string} key - User Profile Key
 * @returns {bool} Result of the find operation on the key
 *
 *
 * @callback inviteeMapFunc
 * @param {string} key - Invitee key
 * @returns {*} Map the key to something you want,
 *
 * @callback userProfileMapFunc
 * @param {string} key - User Profile Key
 * @returns {bool} Map the key to something you want

 * @type {People}
 */

(People as any).FirebaseDataSource = FirebaseDataSource;

export default People;